import { useMemo } from "react";
import { debug as debugFn } from "debug";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { v4 } from "uuid";

import type {
  IncidentWorkItemWithContext,
  WorkflowVersion,
} from "@thalamos/vision-common";
import {
  FormSectionSchema,
  getIncidentTypeSelection,
  IncidentFormIdSchema,
  IncidentWorkItemIdSchema,
} from "@thalamos/vision-common";

import api, { apiHooks } from "../../api/index.js";
import {
  FinishForm,
  FinishFormSkeleton,
} from "../../forms/FinishForm/FinishForm.js";
import { useEndIncidentGuard } from "../../hooks/useEndIncidentGuard.js";
import { useToastNotifications } from "../../hooks/useToastNotifications.js";
import { createRoute } from "../../routing/createRoute.js";
import { Error as ErrorComponent } from "../Error/Error.js";

const debug = debugFn("vision-frontend:FinishFormPage");

export type FinishFormPageProps = Record<string, unknown>;

export function FinishFormPage(_props: FinishFormPageProps) {
  const navigate = useNavigate();
  const params = useParams();

  const incidentWorkItemId = IncidentWorkItemIdSchema.safeParse(params.id);
  if (!incidentWorkItemId.success) {
    throw new Error("Invalid incident work item id");
  }

  const [
    { data: incidentWorkItem, error: incidentWorkItemError },
    refetchWorkItem,
  ] = apiHooks.incidentWorkItem.get(incidentWorkItemId.data);
  const endIncidentGuard = useEndIncidentGuard(
    "finishForm",
    incidentWorkItemId.data,
    incidentWorkItem,
  );
  const toastNotifications = useToastNotifications();
  const { t } = useTranslation();
  const formId = useMemo(() => IncidentFormIdSchema.parse(v4()), []);

  if (incidentWorkItemError) return <ErrorComponent />;
  if (endIncidentGuard.isLeaving) return null;

  const render = (
    incidentWorkItem: IncidentWorkItemWithContext | undefined,
    refetchWorkItem: ReturnType<typeof apiHooks.incidentWorkItem.get>[1],
  ) => {
    const incidentWorkItemId = incidentWorkItem?.incidentWorkItem.id;
    const workflowVersion = incidentWorkItem?.incidentWorkItem
      .workflowVersion as WorkflowVersion | undefined;

    return (
      <>
        {incidentWorkItemId === undefined || workflowVersion === undefined ? (
          <FinishFormSkeleton />
        ) : (
          <FinishForm
            onSubmit={async (values: Record<string, unknown>) => {
              try {
                const response =
                  await api.incidentWorkItem.finishAndCloseIncident(
                    incidentWorkItemId,
                    formId,
                    FormSectionSchema.enum.finishForm,
                    values,
                  );

                if (response.status !== 204) {
                  if (await endIncidentGuard.leaveIfMovedOn()) return;
                  throw Error(JSON.stringify(response.data.message));
                }

                endIncidentGuard.markEnded();
                await refetchWorkItem();

                const incidentTypeSelection = incidentWorkItem
                  ? getIncidentTypeSelection({
                      incidentWorkItem,
                    }).unwrapOr(undefined)
                  : undefined;

                navigate(`/incident/${incidentWorkItemId}/section-complete`, {
                  state: {
                    type: FormSectionSchema.enum.finishForm,
                    payload: {
                      ...values,
                      incidentType: incidentTypeSelection,
                    },
                  },
                });
              } catch (error) {
                debug("Failed to finish and close incident", error);
                toastNotifications.show(
                  t("incidentWorkItem.notificationFailedToSave"),
                  { severity: "error" },
                );
              }
            }}
            onCancel={() => {
              navigate(createRoute.incidentProgressPage(incidentWorkItemId));
            }}
            workflowVersion={workflowVersion}
            incidentWorkItem={incidentWorkItem}
          />
        )}
      </>
    );
  };

  return render(incidentWorkItem, refetchWorkItem);
}
